import { MouseEventHandler, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useDevice } from '@contexts/device';
import { useDebounceValue } from '@hooks/hooks';
import { SEARCH_LENGTH } from '@utils/utils';
import { SearchViewModel } from '@api/v3';

import { BookerSearchViewModel } from '../BookerSearchViewModel';
import DesktopHotelSearchDrawer from './DesktopHotelSearchDrawer';
import MobileHotelSearchDrawer from './MobileHotelSearchDrawer';
import { useGetPopularSearches } from '../../hooks';

type Props = {
  type: string;
  placeholder: string;
  onInputLocationChange?: (item: BookerSearchViewModel) => void;
  responsiveTitle: string;
  fetchItems: (param: string) => Promise<BookerSearchViewModel[]>;
  onHrefUpdate?: (href: string) => void;
  onSearchItemTextUpdate?: (itemText: string) => void;
  history?: BookerSearchViewModel[];
  onClearClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  callBack?: (item: BookerSearchViewModel) => void;
  onRemoveItem?: (item: BookerSearchViewModel) => void;
  error?: boolean;
  href?: string;
  cyprus?: boolean;
  onPopularSearchItemSelected?: (item: SearchViewModel) => void;
};

const HotelSearchDrawer = ({
  placeholder,
  type,
  onInputLocationChange,
  responsiveTitle,
  fetchItems,
  onHrefUpdate,
  onSearchItemTextUpdate,
  history,
  onClearClick,
  callBack,
  onRemoveItem,
  error,
  href,
  cyprus,
  onPopularSearchItemSelected,
}: Props) => {
  const { isDesktop } = useDevice();
  const { items: popularSearchItems } = useGetPopularSearches({
    type: 'hotel',
  });

  const [isShowSearchListingLocation, setIsSHowSearchListingLocation] =
    useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [openAutoCompleteResult, setOpenAutoCompleteResult] =
    useState<boolean>(false);
  const [locationInputError, setLocationInputError] = useState(false);

  const [location, setLocation] = useState<BookerSearchViewModel>();
  const [inputLocation, setInputLocation] = useState<string>(
    location?.text || ''
  );
  const debouncedLocation = useDebounceValue<string>(inputLocation);

  const [selectedLocation, setSelectedLocation] =
    useState<BookerSearchViewModel>();

  const {
    data: locations = [],
    isError: searchTextError,
    isFetching: loadingLocations,
  } = useQuery(
    ['locationItems', debouncedLocation],
    () => fetchItems(debouncedLocation.toLowerCase()),
    { enabled: debouncedLocation.length >= SEARCH_LENGTH }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setInputLocation(e.target.value);

  const handleLocationClicked = (item: BookerSearchViewModel): void => {
    setSelectedLocation(item);
    onSearchItemTextUpdate?.(item.text || '');
    onHrefUpdate?.(item.url || '');
    setInputLocation(item.text || '');
    onInputLocationChange?.(item);

    if (type === 'Tour') {
      const currentHref = href?.split('?')?.[1];
      onHrefUpdate?.(`${item.url}${currentHref ? `?${currentHref}` : ''}`);
      return;
    }
    onHrefUpdate?.(item.url || '');
  };

  const applySearchLocationHandler = () => {
    if (!inputLocation) return setLocationInputError(true);
    setLocation(selectedLocation || { id: -1 });
    onHrefUpdate?.(selectedLocation?.url || '');
    setIsSHowSearchListingLocation(false);

    if (!location?.text) return setLocationInputError(true);
  };

  const handleCloseLocationSearch = (): void => {
    setIsSHowSearchListingLocation(false);
    setInputLocation('');
    onHrefUpdate?.('');
    setOpenAutoCompleteResult(false);
  };

  return isDesktop ? (
    <DesktopHotelSearchDrawer
      locations={locations}
      handleChange={handleChange}
      handleLocationClicked={handleLocationClicked}
      searchTextError={searchTextError}
      placeholder={placeholder}
      inputLocation={inputLocation}
      error={error}
      history={history}
      onClearClick={onClearClick}
      onRemoveItem={onRemoveItem}
      callBack={callBack}
      cyprus={cyprus}
      popularSearchItems={popularSearchItems}
      onPopularSearchItemSelected={onPopularSearchItemSelected}
    />
  ) : (
    <MobileHotelSearchDrawer
      type={type}
      showLocationModal={() => setIsSHowSearchListingLocation(true)}
      width='100%'
      height='100%'
      placement='top'
      title={responsiveTitle}
      visible={isShowSearchListingLocation}
      locationInput={inputLocation || ''}
      handleLocationInputChange={handleChange}
      autoCompleteData={locations}
      autoCompleteItemClicked={handleLocationClicked}
      locationInputError={locationInputError}
      handleUpdateButton={applySearchLocationHandler}
      inputRef={inputRef}
      handleCloseLocationSearch={handleCloseLocationSearch}
      openAutoCompleteResultModal={openAutoCompleteResult}
      setOpenAutoCompleteResultModal={setOpenAutoCompleteResult}
      pastSearchesData={history}
      onClearClick={onClearClick}
      error={error || false}
      isFilled={inputLocation.toString().length > 0}
      callBack={callBack}
      onRemoveItem={onRemoveItem}
      searchTextError={searchTextError}
      searchTextLoading={loadingLocations}
      locationDrawerSubTitle={
        location?.text ? location?.text : placeholder || 'Otel adı veya konum'
      }
      popularSearchItems={popularSearchItems}
      onPopularSearchItemSelected={onPopularSearchItemSelected}
    />
  );
};

export default HotelSearchDrawer;
